import React, { useEffect, useRef } from "react";
import { useTradingViewSymbol } from "./tradingView/useTradingViewSymbol";
import type { TradingViewSymbolOptions } from "./tradingView/tradingViewSymbolSearch";

export interface TradingViewChartWidgetProps extends TradingViewSymbolOptions {
  symbol: string | null | undefined;
}

export const TradingViewChartWidget: React.FC<TradingViewChartWidgetProps> = ({
  symbol,
  referencePrice,
  rejectAbove,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { tradingViewSymbol, isLoading, isError } = useTradingViewSymbol(
    symbol,
    { referencePrice, rejectAbove }
  );

  useEffect(() => {
    if (!containerRef.current || !tradingViewSymbol) {
      return;
    }

    containerRef.current.innerHTML = "";
    const script = document.createElement("script");
    script.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.type = "text/javascript";
    script.async = true;
    script.innerHTML = JSON.stringify({
      allow_symbol_change: false,
      calendar: false,
      details: false,
      hide_side_toolbar: true,
      hide_top_toolbar: false,
      hide_legend: true,
      hide_volume: true,
      hotlist: false,
      interval: "D",
      locale: "en",
      height: 400,
      save_image: false,
      style: "1",
      symbol: tradingViewSymbol,
      theme: "dark",
      timezone: "Etc/UTC",
      backgroundColor: "rgba(26, 27, 32, 1)",
      gridColor: "rgba(46, 47, 53, 1)",
      watchlist: [],
      withdateranges: false,
      compareSymbols: [],
      studies: [],
      autosize: true,
    });

    containerRef.current.appendChild(script);

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
    // The container unmounts while loading, so inject again when it returns.
  }, [tradingViewSymbol, isLoading]);

  if (!symbol || isError || (!tradingViewSymbol && !isLoading)) {
    return (
      <div className="flex items-center justify-center h-[300px] bg-primary/50 rounded-lg">
        <p className="text-text-secondary text-sm">Chart not available</p>
      </div>
    );
  }

  if (isLoading || !tradingViewSymbol) {
    return (
      <div className="flex items-center justify-center h-[300px] bg-primary/50 rounded-lg">
        <p className="text-text-secondary text-sm">Loading chart...</p>
      </div>
    );
  }

  return (
    <div
      className="tradingview-widget-container w-full bg-[#1a1b20]"
      style={{ height: "450px", width: "100%" }}
      ref={containerRef}
    >
      <div
        className="tradingview-widget-container__widget"
        style={{ height: "calc(100% - 32px)", width: "100%" }}
      />
    </div>
  );
};

export default TradingViewChartWidget;
